import { useCallback, useEffect, useRef, useState } from 'react';
import { trainingStatusLabel } from '@flm9/i18n/labels';
import { api, UnauthenticatedError, type LiveAssignment, type RangeSession } from '../lib/api';
import { errorText } from '../lib/errorText';
import { SignInScreen } from './SignInScreen';
import { wakeRefereeSync } from '../lib/sync';

/** Status pill colours (Läuft = green, Geplant = violet, Entwurf = grey). */
const STATUS_BADGE: Partial<Record<RangeSession['status'], string>> = {
  live: 'bg-emerald-900/60 text-emerald-200',
  scheduled: 'bg-violet-900/50 text-violet-200',
  draft: 'bg-zinc-800 text-zinc-300',
};

interface Props {
  /** Tap a session to dive into the roster view for it. */
  onPick: (session: RangeSession, roster: LiveAssignment[]) => void;
  /** After every successful list load — also right after signing in. */
  onSessionsLoaded?: () => void;
}

export function SessionPicker({ onPick, onSessionsLoaded }: Props) {
  const [sessions, setSessions] = useState<RangeSession[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [needsAuth, setNeedsAuth] = useState(false);
  // One-shot guard so a `?s=` deep-link only auto-opens once (e.g. the
  // member's "Schiedsrichter-App öffnen" button jumps straight into their
  // training); after that the user navigates the list normally.
  const autoPickedRef = useRef(false);

  const openSession = useCallback(
    async (s: RangeSession) => {
      try {
        const roster = await api.liveAssignments(s.id);
        onPick(s, roster);
      } catch (err) {
        if (err instanceof UnauthenticatedError) setNeedsAuth(true);
        else setError(errorText(err));
      }
    },
    [onPick],
  );

  const load = useCallback(() => {
    setNeedsAuth(false);
    setError(null);
    api
      .listSessions()
      .then((rows) => {
        wakeRefereeSync();
        onSessionsLoaded?.();
        const open = rows.filter((s) => s.status === 'live' || s.status === 'draft' || s.status === 'scheduled');
        setSessions(open);
        // Honour a `?s=<sessionId>` deep-link: auto-open it if it's in the
        // list the caller is allowed to score.
        if (!autoPickedRef.current) {
          const wanted = new URLSearchParams(window.location.search).get('s');
          const match = wanted ? open.find((s) => s.id === wanted) : undefined;
          if (match) {
            autoPickedRef.current = true;
            void openSession(match);
          }
        }
      })
      .catch((err) => {
        if (err instanceof UnauthenticatedError) setNeedsAuth(true);
        else setError(errorText(err));
      });
  }, [openSession, onSessionsLoaded]);

  useEffect(() => {
    load();
  }, [load]);

  if (needsAuth) {
    return <SignInScreen onSignedIn={load} />;
  }

  if (error) {
    return (
      <div role="alert" className="space-y-3 rounded bg-red-900/30 p-3 text-sm text-red-300">
        <p>Trainings konnten nicht geladen werden: {error}</p>
        <button
          type="button"
          onClick={load}
          className="min-h-12 rounded-lg border border-red-400/60 px-4 py-2 font-semibold text-red-100 hover:bg-red-900/40"
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <section className="space-y-3">
      <h2 className="text-2xl font-bold">Training wählen</h2>
      {sessions.length === 0 ? (
        <p className="text-zinc-500">Aktuell keine offenen Trainings.</p>
      ) : (
        <ul className="space-y-2">
          {sessions.map((s) => (
            <li key={s.id}>
              <button
                onClick={() => void openSession(s)}
                className="flex min-h-12 w-full items-center justify-between gap-3 rounded border border-zinc-700 px-4 py-3 text-left hover:border-emerald-500"
              >
                <span className="font-medium">{s.name}</span>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_BADGE[s.status] ?? 'bg-zinc-800 text-zinc-300'}`}
                >
                  {trainingStatusLabel(s.status)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
